import { LoadingButton } from '@mui/lab';
import { Alert, Card, CardActions, CardContent, CardMedia, Chip, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';

import FormProvider, { RHFTextField } from '@/commons/hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useAuthContext } from '@/hooks/useAuthContext';
import { useCashRegisterContext } from '@/hooks/useCashRegisterContext';
import { getLastCloseCashRegister } from '@/services/cash-register';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { currentFormat } from '@/utils/cases';
import { CashRegister } from '../../types';
import { editCashRegisterSchema, newCashRegisterSchema } from './validations';
import { Button } from '@PorkyCol/button';
import { translates } from '@/utils';
import { cn } from '@PorkyCol/theme';

type FormValuesProps = CashRegister;

type Props = {
  isEdit?: boolean;
  current?: CashRegister;
  isLoading?: boolean;
  onSave: (values: Record<string, unknown>) => Promise<void>;
};

export default function CashRegisterNewEditForm({
  isEdit = false,
  current,
  onSave,
  isLoading,
}: Props) {
  const { isOpen } = useCashRegisterContext();

  const { user } = useAuthContext();
  const name = user?.displayName ?? user?.email?.split('@')[0];

  const { data: closeCashRegister } = useQuery({
    queryKey: ['lastCloseCashRegister'],
    queryFn: getLastCloseCashRegister,
  });

  const methods = useForm<any>({
    defaultValues: {
      userId: name,
      initialCash: closeCashRegister?.cashRegister?.cash,
    },
    resolver: yupResolver(isEdit ? editCashRegisterSchema : newCashRegisterSchema),
  });

  const { reset, handleSubmit, setValue } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    const { initialCash } = data;

    try {
      await onSave({ initialCash, userId: user?.uid });
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setValue('initialCash', closeCashRegister?.cashRegister?.cash);
    }
  }, [closeCashRegister?.cashRegister?.cash, isOpen, setValue]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container direction="column" spacing={3} marginBottom={3}>
        <Grid item xs={12} alignSelf="center">
          <Card sx={{ gap: 2, display: 'flex', flexDirection: 'column', py: isOpen ? 0 : 2 }}>
            {isOpen && <Alert severity="warning">Ya existe una caja abierta</Alert>}
            <CardMedia sx={{ justifyContent: 'center', display: 'flex', mt: isOpen ? 2 : 0 }}>
              <Icon icon="fa6-solid:cash-register" color="#00AB55" width={100} />
            </CardMedia>
            <CardContent
              sx={{ px: 3, gap: 3 }}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <Chip
                variant="outlined"
                color="info"
                label={`El valor de la anterior caja fue de: ${currentFormat(
                  closeCashRegister?.cashRegister?.cash
                )}`}
              />
              <RHFTextField name="userId" value={name} label={translates.fullname} disabled />
              <RHFTextField
                name="initialCash"
                label={translates.initialCash}
                InputLabelProps={closeCashRegister ? { shrink: true } : undefined}
              />
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', mb: isOpen ? 2 : 0 }}>
              <Button type="submit" variant="solid" color="success" loading={isLoading}>
                {!isEdit ? `${translates.newtwo} ${translates['cash-register']}` : 'Save Changes'}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
